/**
 * HTTP Client Configuration for MEV-Med System
 * Axios instance with interceptors for authentication and error handling
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiRequestError, ApiResponse } from '../types/common';
import { mensagemDeRecusa, motivoNoCorpo } from '../utils/acessoNegado';
import { authEvents } from './authEvents';
// Circular: httpClient -> authStore -> authService -> httpClient
// Safe because useAuthStore is only accessed inside runtime callbacks, never at module-init time.
import { useAuthStore } from '../stores/authStore';

// Extended request config with custom options
export interface HttpRequestConfig extends AxiosRequestConfig {
  /**
   * Sem efeito desde T18 da #408: quem define o recorte é a escolha de contexto gravada no
   * servidor, não um cabeçalho montado aqui. O campo continua declarado só porque quatro
   * chamadas ainda o passam; some quando elas forem limpas.
   */
  skipCompanyFilter?: boolean;
}

// API Base Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1';

function getAuthState() {
  return useAuthStore.getState();
}

/** Read access token from Zustand store, with fallback to localStorage persist key */
function getTokenFromStore(): string | null {
  try {
    return getAuthState().accessToken;
  } catch {
    // Store not ready yet (e.g., during initial module load)
    try {
      const stored = localStorage.getItem('mev-auth-store');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.accessToken || null;
      }
    } catch { /* ignore */ }
    return localStorage.getItem('mev_access_token');
  }
}

/** Read refresh token from Zustand store, with fallback to localStorage persist key */
function getRefreshTokenFromStore(): string | null {
  try {
    return getAuthState().refreshToken;
  } catch {
    try {
      const stored = localStorage.getItem('mev-auth-store');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.refreshToken || null;
      }
    } catch { /* ignore */ }
    return localStorage.getItem('mev_refresh_token');
  }
}

/** Update tokens in Zustand store (which also syncs legacy localStorage keys) */
function updateStoreTokens(accessToken: string, refreshToken?: string): void {
  try {
    getAuthState().setTokens(accessToken, refreshToken);
  } catch {
    // Fallback: write directly to localStorage if store not ready
    localStorage.setItem('mev_access_token', accessToken);
    if (refreshToken) {
      localStorage.setItem('mev_refresh_token', refreshToken);
    }
  }
}

/**
 * Requisição com `responseType: 'blob'` (todo download, para poder ler o nome do
 * arquivo no cabeçalho) faz o axios entregar o corpo da resposta como Blob mesmo
 * quando o servidor respondeu erro em JSON — o parsing segue o `responseType` da
 * requisição, decidido antes de o status ser conhecido. Sem isso, `error.response.data`
 * de um download que falhou nunca tem `.message`/`.code`, e o erro vira sempre texto
 * genérico (#386). Se o Blob não for JSON válido (PDF, corpo vazio, texto solto),
 * devolve o Blob como veio — quem chama cai no mesmo texto genérico de hoje.
 */
type CorpoDeErro = Record<string, unknown> | undefined;

/** Lê um campo de texto do corpo do erro, que vem sem forma garantida do servidor. */
function textoDoCorpo(corpo: CorpoDeErro, campo: string): string | undefined {
  const valor = corpo?.[campo];
  return typeof valor === 'string' ? valor : undefined;
}

async function readErrorData(error: { response?: { data?: unknown } }): Promise<CorpoDeErro> {
  const data = error.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      return JSON.parse(await data.text()) as CorpoDeErro;
    } catch {
      // Blob que não é JSON: devolve como veio, preservando o que já chegava antes.
      return data as unknown as CorpoDeErro;
    }
  }
  return data as CorpoDeErro;
}

/**
 * Determine if a refresh error is definitive (auth failure) vs transient (network).
 * Definitive: 400, 401, 403 from the refresh endpoint -> user must re-login.
 * Transient: 5xx, network error, timeout -> don't kill the session.
 */
function isDefinitiveAuthFailure(error: any): boolean {
  if (error?.response?.status) {
    const status = error.response.status;
    return status === 400 || status === 401 || status === 403;
  }
  // No response = network error / timeout -> transient
  return false;
}

interface RefreshSubscriber {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

class HttpClient {
  private client: AxiosInstance;
  private baseURL: string;
  private isRefreshing = false;
  private refreshSubscribers: RefreshSubscriber[] = [];

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private subscribeTokenRefresh(subscriber: RefreshSubscriber): void {
    this.refreshSubscribers.push(subscriber);
  }

  private onTokenRefreshed(token: string): void {
    this.refreshSubscribers.forEach((sub) => sub.resolve(token));
    this.refreshSubscribers = [];
  }

  private onTokenRefreshFailed(error: any): void {
    this.refreshSubscribers.forEach((sub) => sub.reject(error));
    this.refreshSubscribers = [];
  }

  private setupInterceptors(): void {
    // Request interceptor - Add auth token and company header
    this.client.interceptors.request.use(
      (config) => {
        const token = getTokenFromStore();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Log request in development
        if (import.meta.env.DEV) {
          console.log(`[HTTP] ${config.method?.toUpperCase()} ${config.url}`, {
            data: config.data,
            params: config.params,
          });
        }

        return config;
      },
      (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor - Handle responses and errors
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log response in development
        if (import.meta.env.DEV) {
          console.log(`[HTTP] OK ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
          });
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // Log error in development
        if (import.meta.env.DEV) {
          console.error(`[HTTP] ERR ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
            status: error.response?.status,
            message: error.message,
          });
        }

        // Handle 401 errors - Token expired
        if (error.response?.status === 401 && !originalRequest._retry) {
          // Skip refresh for auth endpoints to avoid infinite loop
          if (originalRequest.url?.includes('/auth/refresh') ||
              originalRequest.url?.includes('/auth/login')) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          // If already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.subscribeTokenRefresh({
                resolve: (newToken: string) => {
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  resolve(this.client(originalRequest));
                },
                reject: (err: any) => {
                  reject(err);
                },
              });
            });
          }

          this.isRefreshing = true;

          try {
            const refreshToken = getRefreshTokenFromStore();
            if (!refreshToken) {
              throw { response: { status: 401 }, _noRefreshToken: true };
            }

            const response = await this.refreshAccessToken(refreshToken);
            // Backend returns { message, data: { accessToken, refreshToken?, expiresIn } }
            const newAccessToken = response.data?.data?.accessToken;
            const newRefreshToken = response.data?.data?.refreshToken;

            if (newAccessToken) {
              updateStoreTokens(newAccessToken, newRefreshToken);
              this.isRefreshing = false;
              this.onTokenRefreshed(newAccessToken);
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return this.client(originalRequest);
            }

            // No token in response — treat as definitive failure
            throw { response: { status: 401 }, _emptyResponse: true };
          } catch (refreshError: any) {
            this.isRefreshing = false;

            if (isDefinitiveAuthFailure(refreshError) || refreshError._noRefreshToken || refreshError._emptyResponse) {
              // Definitive: session is dead, signal logout via event bus (no hard reload)
              console.warn('[httpClient] Definitive auth failure during refresh, emitting logout');
              this.onTokenRefreshFailed(refreshError);
              authEvents.emit('auth:logout', { reason: 'refresh_failed' });
            } else {
              // Transient (5xx, network error, timeout): don't kill session, just fail the request
              console.warn('[httpClient] Transient error during refresh, NOT logging out:', refreshError?.message);
              this.onTokenRefreshFailed(refreshError);
            }

            return Promise.reject(refreshError);
          }
        }

        // Transform error to standard format
        const errorData = await readErrorData(error);
        // #408 — a recusa vem com o motivo (permissão, escopo, módulo ou contexto). São quatro
        // saídas diferentes para quem está na tela, e o texto genérico do servidor não as
        // distingue: quem esbarrou no escopo precisa trocar de contexto, não pedir permissão.
        const motivo = error.response?.status === 403 ? motivoNoCorpo(errorData) : undefined;
        const apiError = new ApiRequestError({
          message:
            (motivo && mensagemDeRecusa(motivo)) ??
            (textoDoCorpo(errorData, 'message') ?? textoDoCorpo(errorData, 'error') ?? error.message) ??
            'Erro desconhecido',
          code: textoDoCorpo(errorData, 'code'),
          status: error.response?.status,
          details: errorData,
        });

        return Promise.reject(apiError);
      }
    );
  }

  private async refreshAccessToken(refreshToken: string): Promise<AxiosResponse> {
    // Use raw axios (not this.client) to avoid interceptors re-triggering
    return axios.post(`${this.baseURL}/auth/refresh`, { refreshToken });
  }

  // HTTP Methods
  async get<T = any>(url: string, config?: HttpRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  // File upload
  async uploadFile<T = any>(url: string, file: File, onProgress?: (progress: number) => void): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    };

    const response = await this.client.post<ApiResponse<T>>(url, formData, config);
    return response.data;
  }

  // Download file
  async downloadFile(url: string, filename?: string): Promise<void> {
    const response = await this.client.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Get raw axios instance if needed
  getClient(): AxiosInstance {
    return this.client;
  }

  // Update base URL
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
    this.client.defaults.baseURL = baseURL;
  }
}

// Create and export singleton instance
export const httpClient = new HttpClient();
export default httpClient;
